// modules
import CacheModule from 'modules/Cache';
import ErrorStore from 'modules/Error';
import LocaleStore from 'modules/Localization';
import BodService from 'modules/BOD';
import LoadingStore from 'modules/Loading';
import Fetch from 'modules/Fetch';
// utils
import { objectEntries } from 'utils/helpers';
// typings
import { SearchSegment } from 'modules/TradingChart/components/TradingView/utils/services/symbolsSearch/constants';
import { SearchResultsList } from 'modules/TradingChart/components/TradingView/utils/services/symbolsSearch/typings';

export default class SearchService {
  scripSearch = async (
    queryString: string,
    symbolType: SearchTabKeys = 'ALL',
  ) => {
    try {
      const segment = SearchSegment[symbolType as keyof typeof SearchSegment];

      const response: HttpResponse<SearchResultsList> = await Fetch.request<
        'SEARCH',
        SearchResultsList
      >('SEARCH', '', 'SEARCH_SCRIPS', {
        method: 'GET',
        pathParameter: `?pageNumber=1&query=${queryString}&records=100&segments=${segment}`,
      });

      if (response.success) {
        return response?.data?.searchList;
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  searchByInstrumentKey = async (instrumentKey: string) => {
    if (!instrumentKey) {
      return null;
    }
    // turn on loading
    LoadingStore.setChildLoading('SEARCH_INSTRUMENT_KEY', instrumentKey, true);

    // // check cache results
    const cachedData = this.hasCacheResults<DataSchema>(
      'SEARCH_INSTRUMENT_KEY',
      instrumentKey,
    );

    if (cachedData) {
      // turn of loading
      LoadingStore.setChildLoading(
        'SEARCH_INSTRUMENT_KEY',
        instrumentKey,
        false,
      );

      return cachedData;
    }

    const data = await BodService.searchByInstrumentKey(instrumentKey);

    LoadingStore.setChildLoading('SEARCH_INSTRUMENT_KEY', instrumentKey, false);

    if (!data) {
      ErrorStore.handleError(
        'SEARCH_TOKEN',
        LocaleStore.translate('TOKEN_SEARCH_NOT_FOUND', instrumentKey),
      );
    }

    this.setToCache('SEARCH_INSTRUMENT_KEY', instrumentKey, data);

    return data;
  };

  searchByExchangeToken = async (token: string) => {
    if (!token) {
      return null;
    }
    // turn on loading
    LoadingStore.setChildLoading('SEARCH_TOKEN', token, true);

    // check cache results
    const cachedData = this.hasCacheResults<DataSchema>('SEARCH_TOKEN', token);

    if (cachedData) {
      // turn of loading
      LoadingStore.setChildLoading('SEARCH_TOKEN', token, false);

      return cachedData;
    }

    const data = await BodService.searchExchangeToken(token);

    LoadingStore.setChildLoading('SEARCH_TOKEN', token, false);

    if (!data) {
      ErrorStore.handleError(
        'SEARCH_TOKEN',
        LocaleStore.translate('TOKEN_SEARCH_NOT_FOUND', token),
      );
    }

    this.setToCache('SEARCH_TOKEN', token, data);

    return data;
  };

  private hasCacheResults = <T>(
    cacheKey: SearchCacheKeys = 'SEARCH_ALL',
    rawText: string,
  ) => {
    const nestedKey = `${rawText?.toLowerCase()}`;
    const results = CacheModule.getItem<T>(`SEARCH.${cacheKey}.${nestedKey}`);

    return results;
  };

  protected getTabs(data: SearchResult) {
    return objectEntries(data).reduce(
      (acc, [key, value]) => {
        if (value.length > 0 && key !== 'ALL') {
          acc.push({ title: key });
        }

        return acc;
      },
      [{ title: 'ALL' }] as RenderTabs,
    );
  }

  private setToCache = (
    cacheKey: SearchCacheKeys = 'SEARCH_ALL',
    nestedKey: string,
    data: DataSchema | DataSchema[] | SearchResult | null,
  ) => {
    CacheModule.addItem(`SEARCH.${cacheKey}.${nestedKey.toLowerCase()}`, data);
  };

  public getOptionExpiries = (exchangeToken: string, spotToken: string) =>
    BodService.getOptionExpiries(exchangeToken ?? '', spotToken ?? '');

  public getOptionStrikesForExpiry = (spotToken: string, date: number) =>
    BodService.getOptionStrikesForExpiry(spotToken ?? '', date ?? 0);

  public getFutureScripForExpiry = (spotToken: string) =>
    BodService.getFutureScripForExpiry(spotToken ?? '');
}
